import React from "react";
import type { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import { AlertSeverity } from "../../../../../models/Alerts/Alert";

export interface AlertTone {
  fg: string;
  bg: string;
  border: string;
}

/** Chrome colors for alert surfaces, resolved per light / dark mode. */
export const getAlertColors = (theme: Theme) => {
  const c = theme.palette.customColors;
  const isDark = theme.palette.mode === "dark";
  return {
    isDark,
    surface: isDark ? theme.palette.background.paper : c.white,
    surfaceMuted: isDark ? alpha(c.white, 0.04) : c.pageBackground,
    border: isDark ? alpha(c.white, 0.12) : c.lightGray,
    hairline: isDark ? alpha(c.white, 0.08) : c.lightGreyColor,
    title: isDark ? c.white : c.darkBlueText,
    text: isDark ? alpha(c.white, 0.85) : c.navyText,
    subtitle: isDark ? alpha(c.white, 0.6) : c.grayText,
    muted: isDark ? alpha(c.white, 0.45) : c.mediumGray,
    hover: isDark ? alpha(c.white, 0.06) : c.blueLightBackground,
    unreadBg: isDark ? alpha(c.purple, 0.1) : alpha(c.purple, 0.04),
    accent: c.purple,
    cta: c.darkBlue,
    shadow: `0 12px 32px ${c.shadowLight}, 0 2px 8px ${c.shadowLightBorder}`,
  };
};

export const getSeverityTone = (
  theme: Theme,
  severity: AlertSeverity,
): AlertTone => {
  const c = theme.palette.customColors;
  const fg =
    severity === "HIGH"
      ? c.errorRed
      : severity === "MEDIUM"
        ? theme.palette.orange.main
        : c.darkBlue;
  return { fg, bg: alpha(fg, 0.1), border: alpha(fg, 0.2) };
};

export const getResolvedTone = (theme: Theme): AlertTone => {
  const fg = theme.palette.customColors.successGreen;
  return { fg, bg: alpha(fg, 0.1), border: alpha(fg, 0.2) };
};

export const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
};

const SECTION_ICONS: Record<string, React.ElementType> = {
  orders: ShoppingBagOutlinedIcon,
  order: ShoppingBagOutlinedIcon,
  tasks: TaskAltOutlinedIcon,
  task: TaskAltOutlinedIcon,
  projects: FolderOutlinedIcon,
  project: FolderOutlinedIcon,
  inventory: Inventory2OutlinedIcon,
  shipments: LocalShippingOutlinedIcon,
  shipment: LocalShippingOutlinedIcon,
  logistics: LocalShippingOutlinedIcon,
  finance: AccountBalanceWalletOutlinedIcon,
  payments: AccountBalanceWalletOutlinedIcon,
  vendors: StorefrontOutlinedIcon,
  vendor: StorefrontOutlinedIcon,
  customers: PeopleAltOutlinedIcon,
  invoices: ReceiptLongOutlinedIcon,
  warehouse: WarehouseOutlinedIcon,
  purchase: ShoppingCartOutlinedIcon,
  sales: TrendingUpOutlinedIcon,
  leads: TrendingUpOutlinedIcon,
};

export const SectionIcon: React.FC<{
  icon: string | null | undefined;
  section?: string;
  sx?: object;
}> = ({ icon, section, sx }) => {
  const key = (icon || section || "").toLowerCase();
  const Icon = SECTION_ICONS[key] ?? NotificationsNoneOutlinedIcon;
  return <Icon sx={{ fontSize: 18, ...sx }} />;
};

const IST_FORMAT = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

/** Absolute IST timestamp, e.g. "28 Sept 2026, 11:32 am". */
export const formatAlertDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : `${IST_FORMAT.format(date)} IST`;
};

/** Short relative time for rows: "now", "5m", "3h", "2d", then a date. */
export const formatAlertRelative = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - time) / 1000));
  if (seconds < 60) return "now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
  }).format(new Date(time));
};

export const formatPayloadKey = (key: string): string =>
  key.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
